import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DateFnsUtils from '@date-io/date-fns';
import NumberFormat from 'react-number-format';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  Grid,
  Icon,
  IconButton,
  Tooltip,
  TextField
} from '@material-ui/core';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { Formik } from 'formik';
import * as yup from 'yup';
import { BackdropCircular } from 'components';
import {
  createQuoteProCustomerApi,
  getEavAttributeApi,
  getStoreCustomerGroupsApi
} from 'api';
import { useStyles } from '../styles';


const SearchPatient = ({ open, onClose }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { userStoreData: {storeCode} } = useSelector((state: IReduxState) => state.user);
  const isCustomer3 = storeCode === 'customer3';

  const {
    savingCustomerAccount
  } = useSelector((state: IReduxState) => state.quote);
  const {
    customerGroups,
    loadingCustomerGroups
  } = useSelector((state: IReduxState) => state.customers);
  const {
    options,
    isEavAttributeLoading
  } = useSelector((state: IReduxState) => state.eavAttributes);

  useEffect(() => {
    if (!customerGroups.length) {
      getStoreCustomerGroupsApi({dispatch});
    }

    if (!options?.length) {
      getEavAttributeApi({
        dispatch,
        entityType: 1,
        attributeCode: 'marketing_segments'
      });
    }
  }, []);

  const handleSaveCustomerData = async (values, { setSubmitting }) => {
    const {
      telephone,
      marketingSegment,
      nextech_id,
      patient_source,
      practiceName,
      ...rest
    } = values;

    const customer = {
      ...rest,
      group_id: customerGroups[0].id,
      custom_attributes: [
        { attribute_code: 'phone_number', value: telephone },
        { attribute_code: 'nextech_id', value: nextech_id },
        { attribute_code: 'patient_source', value: patient_source },
        { attribute_code: 'marketing_segments', value: marketingSegment.map((item) => item.value).join(',') }
      ],
    };

    if (isCustomer3) {
      customer.custom_attributes.push({ attribute_code: 'practice_name', value: practiceName });
    }

    const isAccountCreated = await createQuoteProCustomerApi({ dispatch, customer });

    if (isAccountCreated) {
      onClose();
    }

    setSubmitting(false);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle  disableTypography className={classes.dialogTitle}>
        <span>{`Add New ${isCustomer3 ? 'Customer' : 'Patient'}`}</span>
        <IconButton
          edge="end"
          color="inherit"
          size="small"
          onClick={onClose}
          className={classes.closeButton}
          aria-label="close"
        >
          <Icon fontSize="small" className={classes.closeIcon}>close</Icon>
        </IconButton>
      </DialogTitle>

      <DialogContent className="p-0">
        <Formik
          initialValues={{
            firstname: '',
            lastname: '',
            email: '',
            dob: '',
            telephone: '',
            marketingSegment: [],
            nextech_id: '',
            patient_source: '',
            practiceName: ''
          }}
          validationSchema={yup.object({
            firstname: yup
              .string()
              .required('Field is required'),
            lastname: yup
              .string()
              .required('Field is required'),
            email: yup
              .string()
              .email()
              .required('Field is required'),
            dob: isCustomer3
              ? yup.date().typeError('Invalid date')
              : yup.date().typeError('Invalid date').required('Field is required'),
            telephone: yup
              .string()
              .min(11, 'Enter valid phone number')
              .max(11, 'Enter valid phone number')
              .required('Field is required'),
            practiceName: isCustomer3 ?
              yup.string().required('Field is required')
              : null,
            marketingSegment: yup
              .array()
          })}
          onSubmit={handleSaveCustomerData}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
            isSubmitting,
          }) => (
            <form noValidate autoComplete="off" onSubmit={handleSubmit}>
              <div className="py-16 px-12">
                <Grid container spacing={3} className="flex items-center">
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      variant="outlined"
                      label="First Name"
                      name="firstname"
                      value={values.firstname}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched?.firstname && Boolean(errors?.firstname)}
                      helperText={touched?.firstname && errors?.firstname}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      variant="outlined"
                      label="Last Name"
                      name="lastname"
                      value={values.lastname}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched?.lastname && Boolean(errors?.lastname)}
                      helperText={touched?.lastname && errors?.lastname}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      variant="outlined"
                      label="Email Address"
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched?.email && Boolean(errors?.email)}
                      helperText={touched?.email && errors?.email}
                    />
                  </Grid>
                  {isCustomer3 &&
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        variant="outlined"
                        label="Company/Practice Name"
                        name="practiceName"
                        value={values.practiceName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched?.practiceName && Boolean(errors?.practiceName)}
                        helperText={touched?.practiceName && errors?.practiceName}
                      />
                    </Grid>
                  }
                  <Grid item xs={12} sm={6} container>
                    <Grid item xs={11}>
                      <FormControl fullWidth variant="outlined" required>
                        <NumberFormat
                          customInput={TextField}
                          format="+# (###) ###-####"
                          required
                          fullWidth
                          label="Mobile Number"
                          variant="outlined"
                          name="telephone"
                          value={values.telephone || null}
                          onValueChange={(value) => {
                            setFieldValue('telephone', String(value.floatValue));
                          }}
                          onBlur={handleBlur}
                          error={touched?.telephone && Boolean(errors?.telephone)}
                          helperText={touched?.telephone && errors?.telephone}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={1}>
                      <Tooltip
                        title="Please enter your patient mobile number. It will be used for SMS verification to log into the Offers Page."
                        arrow
                      >
                        <IconButton aria-label="info">
                          <Icon fontSize="small">info</Icon>
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} sm={6} container>
                    <Grid item xs={isCustomer3 ? 11 : 12}>
                      <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <DatePicker
                          variant="inline"
                          inputVariant="outlined"
                          format="MM-dd-yyyy"
                          autoOk
                          disableFuture
                          required={!isCustomer3}
                          fullWidth
                          label="Date of Birth"
                          value={values.dob || null}
                          onChange={(date) => {
                            setFieldValue('dob', date.toLocaleDateString('en-US'));
                          }}
                          onBlur={handleBlur}
                          error={touched?.dob && Boolean(errors?.dob)}
                          helperText={touched?.dob && errors?.dob}
                        />
                      </MuiPickersUtilsProvider>
                    </Grid>

                    {isCustomer3 && (
                      <Grid item xs={1}>
                        <Tooltip
                          title="Default value (01/01/1971) will be used if not specified."
                          arrow
                        >
                          <IconButton aria-label="info">
                            <Icon fontSize="small">info</Icon>
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      multiple
                      fullWidth
                      disableClearable
                      size="small"
                      value={values.marketingSegment}
                      classes={{
                        inputRoot: classes.marketingSegment
                      }}
                      options={options}
                      getOptionLabel={(option) => option.label}
                      getOptionSelected={(option, value) => option.value === value.value}
                      filterSelectedOptions
                      onChange={(e, value) => setFieldValue('marketingSegment', value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Marketing Segment"
                          variant="outlined"
                          error={touched.marketingSegment && Boolean(errors.marketingSegment)}
                          helperText={touched.marketingSegment && errors.marketingSegment}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <NumberFormat
                      customInput={TextField}
                      fullWidth
                      decimalScale={0}
                      fixedDecimalScale
                      allowNegative={false}
                      label="Nextech Patient ID"
                      variant="outlined"
                      name="nextech_id"
                      value={values.nextech_id}
                      onValueChange={(value) => {
                        setFieldValue('nextech_id', String(value.floatValue));
                      }}
                      onBlur={handleBlur}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Patient Source"
                      name="patient_source"
                      value={values.patient_source}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Grid>
                </Grid>
              </div>
              <div className={`${classes.dialogTitle} p-4`}>
                <Button variant="outlined" color="primary" className="uppercase" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className="uppercase"
                  disabled={isSubmitting || Object.keys(errors).length > 0}
                >
                  Add
                </Button>
              </div>
            </form>
          )}
        </Formik>
        <BackdropCircular loading={loadingCustomerGroups || savingCustomerAccount || isEavAttributeLoading} />
      </DialogContent>
    </Dialog>
  );
};

export default SearchPatient;
